import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';

/**
 * Custom element that renders the variant-level "In Store Only" notice.
 *
 * The notice is rendered server-side by `blocks/buy-buttons.liquid` from
 * the variant metafield `custom.in_store_only`. When the variant picker
 * fetches the updated product section, this element morphs itself from
 * the matching element in the new HTML so the badge stays in sync with
 * the currently-selected variant.
 */
class InStoreOnlyNotice extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    const target = this.closest('.shopify-section, dialog');
    if (!target) return;

    this.#abortController = new AbortController();
    target.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });
    // Also listen on document as a fallback in case the dispatch path skips
    // the section (the custom swatch picker dispatches on the variantPicker
    // and section, but we want to be tolerant of variations).
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * @param {Event} event - VariantUpdateEvent from `@theme/events`.
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (!detail?.data?.html) return;

    // Only react to updates for our own product (avoids cross-talk on
    // combined listings or product cards in recommendations).
    const productId = detail.data.newProduct?.id ?? detail.data.productId;
    if (productId && this.dataset.productId && productId !== this.dataset.productId) {
      return;
    }

    const newNotice = detail.data.html.querySelector(
      `in-store-only-notice[data-product-id="${this.dataset.productId}"]`
    ) ?? detail.data.html.querySelector('in-store-only-notice');

    if (!newNotice) {
      // No notice in the new HTML means the new variant is sellable online.
      this.setAttribute('hidden', '');
      this.#syncBuyButtons(null);
      return;
    }

    // The theme's morph runs with `childrenOnly: true`, so attributes on the
    // wrapper itself (notably `hidden`) aren't synced by morph — handle them
    // explicitly before morphing the inner content.
    if (newNotice.hasAttribute('hidden')) {
      this.setAttribute('hidden', '');
    } else {
      this.removeAttribute('hidden');
    }

    morph(this, newNotice);
    this.#syncBuyButtons(detail.data.html);
  };

  /**
   * Mirror the buy-button wrapper's `hidden` state from the incoming HTML.
   *
   * The wrapper is always rendered (see `blocks/buy-buttons.liquid`) and only
   * toggles `hidden`, so that switching away from an In Store Only variant
   * restores the buttons. As with the notice above, morph's `childrenOnly`
   * means the attribute has to be copied across by hand.
   *
   * @param {Document | DocumentFragment | null} newHtml - incoming section HTML,
   *   or null when the new variant is known to be sellable online.
   */
  #syncBuyButtons(newHtml) {
    const form = this.closest('form') ?? this.closest('product-form-component');
    const current = form?.querySelector('[data-in-store-only-hideable]');
    if (!current) return;

    const incoming = newHtml?.querySelector('[data-in-store-only-hideable]');
    // No incoming wrapper to compare against: fall back to the notice's own
    // state, which has already been resolved for this variant.
    const shouldHide = incoming
      ? incoming.hasAttribute('hidden')
      : !this.hasAttribute('hidden');

    if (shouldHide) {
      current.setAttribute('hidden', '');
    } else {
      current.removeAttribute('hidden');
    }
  }
}

if (!customElements.get('in-store-only-notice')) {
  customElements.define('in-store-only-notice', InStoreOnlyNotice);
}
